/**
 * Unità di misura per la quantità di un prodotto in offerta.
 * Lista canonica suggerita nei select, default 'pz'.
 * Il campo DB (deal_services.unit_of_measure) è TEXT libero: l'utente può
 * digitare un'unità custom (es. "sacchetti da 20kg") non presente in lista.
 */
export const UNITS_OF_MEASURE = ['pz', 'kg', 'g', 'ton', 'kton', 'l', 'ml', 'm', 'm²', 'm³', 'h', 'gg', 'conf', 'cartone', 'sacco', 'altro'] as const;

export type UnitOfMeasure = typeof UNITS_OF_MEASURE[number];

export const DEFAULT_UNIT_OF_MEASURE: UnitOfMeasure = 'pz';

/**
 * Elenco fisso storico dei menu UM nei form di magazzino (DDT, ordini
 * fornitore, distinte, produzione, lotti). Resta quello mostrato alle org
 * senza unità predefinita, vedi useOrgUnitOfMeasure.unitOptions.
 */
export const LEGACY_WAREHOUSE_LINE_UNITS = ['pz', 'kg', 'lt', 'm', 'q', 't'] as const;

/**
 * Opzioni UM per i select: lista canonica + eventuale valore custom già
 * salvato (aggiunto in testa se non presente in lista).
 */
export function getUnitOptions(current?: string | null): string[] {
  const base: string[] = [...UNITS_OF_MEASURE];
  if (current && !base.includes(current)) base.unshift(current);
  return base;
}

/**
 * L'unità di misura di un articolo di magazzino, con fallback.
 * La UDM 1 è quella primaria dell'anagrafica (products.unit_of_measure_1);
 * la UDM 2 esiste solo come termine di conversione e non va usata qui.
 */
export function resolveProductUnit(
  product?: { unit_of_measure_1?: string | null } | null,
): string {
  return product?.unit_of_measure_1?.trim() || DEFAULT_UNIT_OF_MEASURE;
}

/**
 * Opzioni UM quando si conoscono le unità configurate dall'organizzazione
 * (tabella `units_of_measure`, la stessa che alimenta la scheda articolo).
 * Sono quelle a comandare: la lista canonica resta come rete di sicurezza
 * finché la tabella non è caricata o è vuota. Il valore corrente entra
 * comunque in testa, così un'unità storica non sparisce dal menu.
 */
export function buildUnitOptions(current?: string | null, orgUnits?: string[] | null): string[] {
  return withCurrentUnit(current, orgUnits && orgUnits.length > 0 ? orgUnits : UNITS_OF_MEASURE);
}

/*
  Unità scritte in modi diversi ma equivalenti (28/10/2026, Ideariso): l'anagrafica articolo importata usa
  «pezzi» e «KG», le unità dell'org «pz» e «kg», e i menu mostravano entrambe. Stessa tabella di sinonimi di
  flusia-core (domains/warehouse/unitAlias.ts), che già le tratta come la stessa unità nei carichi e scarichi.
*/
const UNIT_ALIASES: Record<string, string> = {
  pz: 'pz', 'pz.': 'pz', pezzi: 'pz', pezzo: 'pz', pcs: 'pz', pc: 'pz', n: 'pz', 'n.': 'pz', nr: 'pz', 'nr.': 'pz',
  l: 'l', lt: 'l', 'lt.': 'l', litri: 'l', litro: 'l',
  kg: 'kg', 'kg.': 'kg', chilogrammi: 'kg', chilogrammo: 'kg', kilogrammi: 'kg', kilogrammo: 'kg', chili: 'kg',
  g: 'g', gr: 'g', 'gr.': 'g', grammi: 'g', grammo: 'g',
};

/** Chiave di confronto di un'unità: minuscola, senza spazi, sinonimi ricondotti a una forma sola («pezzi» → «pz»). */
export function unitKey(unit: string | null | undefined): string {
  const u = (unit ?? '').trim().toLowerCase();
  return UNIT_ALIASES[u] ?? u;
}

/** Due unità equivalenti («pz» e «pezzi», «KG» e «kg»). */
export function sameUnit(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a?.trim() && !!b?.trim() && unitKey(a) === unitKey(b);
}

/** La voce di un elenco equivalente a `unit`, se c'è («pezzi» in [pz, kg] → «pz»). */
export function matchUnit(unit: string | null | undefined, options: readonly string[]): string | undefined {
  return unit?.trim() ? options.find(o => sameUnit(o, unit)) : undefined;
}

/**
 * Elenco per un menu UM: una sola voce per unità (i sinonimi si tolgono) e il valore corrente sempre presente.
 * Se il valore salvato è un sinonimo di una voce («pezzi» con «pz» in elenco) prende il suo posto, così il menu
 * mostra il valore della riga senza una seconda voce equivalente.
 */
export function withCurrentUnit(current: string | null | undefined, base: readonly string[]): string[] {
  const list: string[] = [];
  for (const unit of base) if (!list.some(u => sameUnit(u, unit))) list.push(unit);
  const value = current?.trim();
  if (!value) return list;
  const index = list.findIndex(u => sameUnit(u, value));
  if (index >= 0) list[index] = value;
  else list.unshift(value);
  return list;
}
